import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

// Complete test definitions extracted from example.report.html.php and language_file.php
const testDefinitions = {
  // GENERAL TESTS
  language: {
    name: 'Language attribute',
    help: 'The language attribute tells browsers and search engines your webpage\'s main language, improving accessibility and accuracy.',
    issues: {
      missing: { severity: 'minor', message: 'Language attribute is missing.', help: 'Make sure to implement the lang attribute in your <html> tag by using an ISO 639-1 language code.' }
    }
  },
  meta_charset: {
    name: 'Meta charset',
    help: 'The meta charset tag ensures your website uses the correct character encoding, preventing broken or garbled text and improving user experience.',
    issues: {
      missing: { severity: 'moderate', message: 'Meta charset tag is missing.', help: 'Make sure to add a meta charset tag in your <head> section using the <meta charset="UTF-8"> attribute to ensure proper text rendering across all browsers.' }
    }
  },
  meta_viewport: {
    name: 'Meta viewport',
    help: 'The meta viewport tag ensures your website adapts to different screen sizes, improving mobile usability and user experience, which are crucial for SEO and engagement.',
    issues: {
      missing: { severity: 'moderate', message: 'Meta viewport tag is missing.', help: 'Make sure to add a meta viewport tag in your <head> section using the <meta name="viewport" content="width=device-width, initial-scale=1"> tag to ensure proper display on all devices.' }
    }
  },
  favicon: {
    name: 'Favicon',
    help: 'The favicon is a small icon that represents your website in browser tabs, bookmarks, and search results, improving branding and user recognition.',
    issues: {
      missing: { severity: 'moderate', message: 'Favicon is missing.', help: 'Make sure to add a favicon by linking a small icon file in your <head> section using the <link> tag.' }
    }
  },
  doctype: {
    name: 'Doctype declaration',
    help: 'The doctype declaration tells the browser about what document type to expect.',
    issues: {
      missing: { severity: 'moderate', message: 'Doctype is missing.', help: 'Make sure to add the <!DOCTYPE html> snippet to the start of your HTML webpage content.' }
    }
  },
  deprecated_html_tags: {
    name: 'Deprecated HTML tags',
    help: 'Deprecated HTML tags are outdated elements no longer recommended for use, as they are replaced by modern alternatives to improve web standards and functionality.',
    issues: {
      existing: { severity: 'moderate', message: 'Deprecated tags found.', help: 'Remove deprecated tags to prevent rendering issues & improve compatibility with modern browsers, which helps search engines index your content effectively & user experience.' }
    }
  },
  header_server: {
    name: 'Server tag',
    help: 'This header tag should not be included in your webpage to avoid being used by attackers to find potential vulnerabilities.',
    issues: {
      existing: { severity: 'minor', message: 'Server tag is public.', help: 'Make sure to remove the server HTTP header tag to avoid exposing details about your server.' }
    }
  },
  server_compression: {
    name: 'Server compression',
    help: 'Server compression, like gzip or Brotli, reduces the size of web resources, leading to faster load times.',
    issues: {
      missing: { severity: 'moderate', message: 'No compression algorithm found.', help: 'Make sure to enable server response compression with modern algorithms, such as brotli or gzip.' }
    }
  },

  // SEO TESTS
  title: {
    name: 'Title',
    help: 'The title tag is crucial for SEO as it appears in search results and browser tabs.',
    issues: {
      missing: { severity: 'major', message: 'Title tag is missing.', help: 'It is important to have a page title. Make sure to add one with 50-60 characters.' },
      too_long: { severity: 'major', message: 'Title is too long.', help: 'It is recommended to keep your title between 50-60 characters.' },
      too_short: { severity: 'major', message: 'Title is too short.', help: 'It is recommended to have at least 30 characters in your title. Ideally between 50-60 characters.' }
    }
  },
  meta_description: {
    name: 'Meta description',
    help: 'Meta description appears in search results and impacts click-through rates.',
    issues: {
      missing: { severity: 'major', message: 'Meta description tag is missing.', help: 'It is important to have a meta description on your page. Make sure to add one with 50-160 characters.' },
      too_long: { severity: 'moderate', message: 'Meta description is too long.', help: 'It is recommended to keep your meta description between 50-160 characters.' },
      too_short: { severity: 'moderate', message: 'Meta description is too short.', help: 'It is recommended to have at least 50 characters in your meta description. Ideally between 50-160 characters.' }
    }
  },
  h1: {
    name: 'H1 tag',
    help: 'H1 heading is important for SEO and page structure.',
    issues: {
      missing: { severity: 'major', message: 'H1 tag is missing.', help: 'It is important to have one H1 heading tag on your page. Make sure to add one with 50± characters.' },
      too_many: { severity: 'moderate', message: 'Too many H1 tags.', help: 'It is significant to have only one H1 heading tag on your page.' },
      too_long: { severity: 'minor', message: 'H1 tag is too long.', help: 'It is trivial to keep your H1 tag between 10-120 characters.' },
      too_short: { severity: 'minor', message: 'H1 tag is too short.', help: 'It is trivial to have at least 10 characters in your H1 tag. Ideally between 10-120 characters.' }
    }
  },
  meta_keywords: {
    name: 'Meta keywords',
    help: 'Meta keywords are no longer relevant for Google SEO; however, other search engines, such as Baidu and Yandex, still consider them.',
    issues: {
      missing: { severity: 'minor', message: 'Meta keywords are missing.', help: 'Meta keywords are no longer relevant for Google SEO; however, other search engines, such as Baidu and Yandex, still consider them.' }
    }
  },
  meta_robots: {
    name: 'Meta robots',
    help: 'Meta robots tags control how search engines index your page.',
    issues: {
      excluded: { severity: 'major', message: 'Page is excluded from indexing.', help: 'Make sure to remove the noindex and none properties to allow search engines to index your webpage.' }
    }
  },
  header_robots: {
    name: 'Header robots',
    help: 'We tested your webpage for the noindex and none properties on the X-Robots-Tag header.',
    issues: {
      excluded: { severity: 'major', message: 'Page is excluded from indexing.', help: 'Make sure to remove the noindex and none properties from the X-Robots-Tag header tag to allow search engines to index your webpage.' }
    }
  },
  robots: {
    name: 'robots.txt page',
    help: 'The robots.txt file tells crawlers which pages can be accessed.',
    issues: {
      missing: { severity: 'minor', message: 'robots.txt page is not implemented.', help: 'The robots.txt file can tell crawlers which pages can be accessed or not. If you don\'t have one, all public pages within your website will be crawled.' },
      excluded: { severity: 'major', message: 'Page is excluded from crawling.', help: 'The robots.txt directives have excluded this page from being crawled by bots.' }
    }
  },
  not_found: {
    name: '404 page',
    help: 'We tested your site against a randomized URL on your webpage so that we can validate if your site returns a 404 status code.',
    issues: {
      missing: { severity: 'moderate', message: '404 valid pages are not implemented.', help: 'A page that does not exist on your website should return a valid 404 status code.' }
    }
  },
  canonical: {
    name: 'Canonical URL',
    help: 'The canonical tag prevents duplicate content by specifying the preferred URL.',
    issues: {
      missing: { severity: 'minor', message: 'Canonical tag is missing.', help: 'Make sure to add the link tag in your <head> section using the rel="canonical" attribute.' }
    }
  },
  is_seo_friendly_url: {
    name: 'SEO friendly URL',
    help: 'SEO-friendly URLs are clean, readable, lowercase, and use hyphens.',
    issues: {
      false: { severity: 'minor', message: 'URL is not search engine optimized.', help: 'Make sure to remove any uppercase or special characters, only use hyphens to separate words.' }
    }
  },
  opengraph: {
    name: 'Open graph',
    help: 'Open Graph tags optimize how your content appears on social media platforms.',
    issues: {
      missing: { severity: 'minor', message: 'Open Graph tags are missing.', help: 'Add essential Open Graph tags like og:title, og:description, og:image, and og:url to improve social media sharing and user engagement.' }
    }
  },
  schemas: {
    name: 'Schema.org structured data',
    help: 'Schema.org structured data helps search engines understand your content better.',
    issues: {
      missing: { severity: 'minor', message: 'Schema.org tags are missing.', help: 'Implement structured data using Schema.org to enhance search engine indexing, enable rich snippets, and improve content discoverability.' }
    }
  },
  other_headings: {
    name: 'Other headings',
    help: 'H2 to H6 headings should be used across your webpage to properly structure your content.',
    issues: {}
  },

  // PERFORMANCE TESTS
  response_time: {
    name: 'Response time',
    help: 'Response time is crucial for SEO; faster pages improve rankings, user experience, and reduce bounce rates.',
    issues: {
      too_slow: { severity: 'major', message: 'Response time is too slow.', help: 'It is important to keep your response time below 2 seconds.' }
    }
  },
  page_size: {
    name: 'Page size',
    help: 'A smaller page HTML size ensures faster initial page load, improving user experience.',
    issues: {
      too_big: { severity: 'moderate', message: 'Page size is too big.', help: 'It is recommended to keep your page size below 150 kB (kilobytes).' }
    }
  },
  dom_size: {
    name: 'DOM size',
    help: 'A smaller DOM size ensures faster rendering, smoother interactivity, and better performance.',
    issues: {
      too_big: { severity: 'minor', message: 'DOM size is too big.', help: 'It is recommended to keep your DOM size below 1,500 nodes.' }
    }
  },
  http_requests: {
    name: 'HTTP requests',
    help: 'HTTP requests are calls a webpage makes to fetch resources, and too many slow down loading.',
    issues: {
      too_many: { severity: 'moderate', message: 'Too many HTTP requests.', help: 'Consider optimizing your assets by minifying, combining files, and using CDNs where possible to improve page performance.' }
    }
  },
  is_http2: {
    name: 'HTTP/2 enabled',
    help: 'HTTP/2 improves website performance by enabling faster page load times.',
    issues: {
      missing: { severity: 'moderate', message: 'HTTP/2 is not enabled.', help: 'Enable HTTP/2 support on your server for faster performance and improved SEO benefits.' }
    }
  },

  // SECURITY TESTS
  is_https: {
    name: 'Secure URL (HTTPS)',
    help: 'HTTPS secures data with encryption, boosts trust, and improves SEO rankings.',
    issues: {
      missing: { severity: 'major', message: 'No HTTPS encryption.', help: 'It is important to get an SSL certificate for your domain.' }
    }
  },
  is_ssl_valid: {
    name: 'Valid SSL Certificate',
    help: 'A valid SSL certificate ensures secure connections and builds user trust.',
    issues: {
      invalid: { severity: 'major', message: 'Invalid or expired SSL certificate.', help: 'Renew or replace your SSL certificate to maintain website security and trustworthiness.' }
    }
  },
  safe_browsing: {
    name: 'Safe browsing',
    help: 'Google Safe Browsing checks if the website is flagged for malware, phishing, or other security threats.',
    issues: {
      false: { severity: 'major', message: 'URL is not safe.', help: 'Google has marked your web page as not safe. You must investigate why and try to solve this issue as fast as possible.' }
    }
  },

  // CONTENT TESTS
  inline_css: {
    name: 'Inline CSS',
    help: 'Using inline CSS can make your webpage harder to maintain and may negatively impact loading performance.',
    issues: {
      existing: { severity: 'minor', message: 'Inline CSS found.', help: 'Avoid using inline CSS. Move styles to external stylesheets for better performance and maintainability.' }
    }
  },
  image_formats: {
    name: 'Image formats',
    help: 'AVIF, WebP and SVG optimize SEO by reducing image sizes for faster loading.',
    issues: {
      existing: { severity: 'moderate', message: 'Unoptimized images found.', help: 'Convert your images to WebP, AVIF or SVG formats.' }
    }
  },
  image_alt: {
    name: 'Image alt attributes',
    help: 'Alt attributes improve SEO by describing images for search engines.',
    issues: {
      missing: { severity: 'moderate', message: 'Images with missing alt attributes found.', help: 'Add descriptive alt attributes to your images for better SEO and accessibility.' }
    }
  },
  image_lazy_loading: {
    name: 'Lazy loading images',
    help: 'The loading="lazy" attribute improves SEO and page speed by deferring the loading of images.',
    issues: {
      missing: { severity: 'minor', message: 'Images without lazy loading found.', help: 'Add the loading="lazy" attribute to your images to improve page performance and SEO. Only use this attribute for images below-the-fold.' }
    }
  },
  unsafe_external_links: {
    name: 'Unsafe external links',
    help: 'External links with target="_blank" should include noopener or noreferrer.',
    issues: {
      existing: { severity: 'minor', message: 'Unsafe links found.', help: 'Make sure to add the rel="noopener" or rel="noreferrer" attribute to the link tag to avoid potential security issues.' }
    }
  },
  non_deferred_scripts: {
    name: 'Non-deferred javascript',
    help: 'JavaScript scripts should use the defer attribute to avoid blocking page rendering.',
    issues: {
      existing: { severity: 'minor', message: 'Non-deferred scripts found.', help: 'Add the defer attribute to JavaScript script tags to improve page load performance and prevent render-blocking issues.' }
    }
  },
  internal_links: {
    name: 'Internal links',
    help: 'Internal links connect pages within your website. Too many can dilute their impact.',
    issues: {
      too_many: { severity: 'minor', message: 'Too many internal links.', help: 'Reduce internal links to below 100 for better SEO and crawl efficiency.' }
    }
  },
  external_links: {
    name: 'External links',
    help: 'External links point to other websites. Too many may dilute your page\'s authority.',
    issues: {
      too_many: { severity: 'minor', message: 'Too many external links.', help: 'Limit external links to 25 or fewer to retain page authority.' }
    }
  },
  social_links: {
    name: 'Social links',
    help: 'Social media links help with brand presence and engagement.',
    issues: {
      missing: { severity: 'minor', message: 'Social links are missing.', help: 'We have not found any relevant social links on this webpage.' }
    }
  },
  words_count: {
    name: 'Words count',
    help: 'It is significant to have at least 300-500 words on your webpage.',
    issues: {
      too_few: { severity: 'moderate', message: 'Too few words.', help: 'Make sure to create more content for your webpage. Aim for at least 300-500 words on your webpage.' }
    }
  },
  words_used: {
    name: 'Top used words',
    help: 'Shows the most frequently used keywords on the page.',
    issues: {
      missing: { severity: 'minor', message: 'Words are missing.', help: 'We have not found any relevant words on this webpage.' }
    }
  },
  emails: {
    name: 'Plaintext emails',
    help: 'Plaintext emails on webpages are bad because they can be easily scraped by bots.',
    issues: {
      existing: { severity: 'minor', message: 'Plaintext emails found.', help: 'Remove or replace all plaintext emails from your webpage to prevent spam & abuse by bots.' }
    }
  },
  text_to_html_ratio: {
    name: 'Text to HTML ratio',
    help: 'A higher text-to-HTML ratio signals valuable content, efficient coding, and better user experience.',
    issues: {
      too_low: { severity: 'moderate', message: 'Text to HTML ratio is too low.', help: 'It is good to have a higher than 15% text to HTML ratio.' }
    }
  }
};

const CollapsibleList = ({ count, children, label }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!count || count === 0) return null;

  return (
    <div className="mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium text-gray-700 flex items-center justify-between transition"
      >
        <span>{label.replace('%s', count)}</span>
        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {isExpanded && (
        <div className="mt-2 p-4 bg-white border border-gray-200 rounded">
          {children}
        </div>
      )}
    </div>
  );
};

const AuditReportSection = ({ audit }) => {
  if (!audit || !audit.issues) return null;

  const getTestIcon = (testName, issues) => {
    const hasIssue =
      (issues?.major && issues.major[testName]) ||
      (issues?.moderate && issues.moderate[testName]) ||
      (issues?.minor && issues.minor[testName]);

    if (hasIssue) {
      const isMajor = issues?.major && issues.major[testName];
      const isModerate = issues?.moderate && issues.moderate[testName];

      if (isMajor) return <XCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />;
      else if (isModerate) return <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" />;
      else return <AlertCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" />;
    }

    return <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />;
  };

  const renderIssue = (severity, message, help) => {
    const severityConfig = {
      major: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-900' },
      moderate: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-900' },
      minor: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900' }
    };

    const config = severityConfig[severity];

    return (
      <div className={`mb-3 p-3 rounded border ${config.bg} ${config.border}`}>
        <p className={`text-sm font-medium m-0 ${config.text}`}>{message}</p>
        <small className="text-gray-600">{help}</small>
      </div>
    );
  };

  const renderTest = (testName, testDef) => {
    const auditData = audit.data || {};
    const issues = audit.issues || {};

    const hasIssues =
      (issues.major && issues.major[testName]) ||
      (issues.moderate && issues.moderate[testName]) ||
      (issues.minor && issues.minor[testName]);

    const hasData = auditData[testName] !== undefined && auditData[testName] !== null;

    // Skip if no issues and no data
    if (!hasIssues && !hasData) return null;

    return (
      <div key={testName} className="border-b border-gray-100 py-4 last:border-0">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left: Test Name */}
          <div className="lg:w-1/3">
            <div className="flex items-start">
              {getTestIcon(testName, issues)}
              <div>
                <div className="font-semibold text-gray-900">{testDef.name}</div>
                <div className="text-xs text-gray-500 mt-1">{testDef.help}</div>
              </div>
            </div>
          </div>

          {/* Right: Data and Issues */}
          <div className="lg:w-2/3">
            {/* Display actual data if available */}
            {hasData && (
              <div className="mb-3">
                {/* Title */}
                {testName === 'title' && auditData.title && (
                  <div>
                    <p className="text-sm font-medium text-gray-800">{auditData.title}</p>
                    <small className="text-gray-500">{auditData.title.length} characters</small>
                  </div>
                )}

                {/* Meta Description */}
                {testName === 'meta_description' && auditData.meta_description && (
                  <div>
                    <p className="text-sm text-gray-800">{auditData.meta_description}</p>
                    <small className="text-gray-500">{auditData.meta_description.length} characters</small>
                  </div>
                )}

                {/* H1 */}
                {testName === 'h1' && Array.isArray(auditData.headings?.h1) && auditData.headings.h1.length > 0 && (
                  <div>
                    {auditData.headings.h1.map((h1, idx) => (
                      <div key={idx} className="mb-2">
                        <p className="text-sm font-medium text-gray-800">{h1}</p>
                        <small className="text-gray-500">{h1.length} characters</small>
                      </div>
                    ))}
                  </div>
                )}

                {/* Language */}
                {testName === 'language' && auditData.language && (
                  <div>
                    <p className="text-sm font-medium text-gray-800">{auditData.language}</p>
                  </div>
                )}

                {/* Meta Charset */}
                {testName === 'meta_charset' && auditData.meta_charset && (
                  <div>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">{auditData.meta_charset}</code>
                  </div>
                )}

                {/* Meta Viewport */}
                {testName === 'meta_viewport' && auditData.meta_viewport && (
                  <div>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">{auditData.meta_viewport}</code>
                  </div>
                )}

                {/* DOM Size */}
                {testName === 'dom_size' && auditData.dom_size && (
                  <div>
                    <p className="text-sm font-medium text-gray-800">{auditData.dom_size} nodes</p>
                  </div>
                )}

                {/* Favicon */}
                {testName === 'favicon' && auditData.favicon && (
                  <div>
                    <img src={auditData.favicon} alt="Favicon" className="w-4 h-4 inline-block mr-2" />
                    <a href={auditData.favicon} target="_blank" rel="nofollow noreferrer" className="text-sm text-blue-600 hover:underline">{auditData.favicon}</a>
                  </div>
                )}
              </div>
            )}

            {/* Expandable lists for specific tests */}
            {testName === 'image_formats' && auditData.images && hasIssues && (() => {
              const unoptimizedImages = auditData.images.filter(img =>
                img.extension && !['webp', 'avif', 'svg'].includes(img.extension.toLowerCase())
              );
              return (
                <CollapsibleList count={unoptimizedImages.length} label={`${unoptimizedImages.length} unoptimized images found`}>
                  {unoptimizedImages.map((img, idx) => (
                    <div key={idx} className="flex justify-between items-start mb-3 pb-3 border-b last:border-0">
                      <div className="flex-1 text-truncate">
                        <p className="text-sm mb-1">
                          <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono mr-2">{img.extension}</span>
                          {img.title || 'No title set'}
                        </p>
                        <small className="text-gray-500 truncate block">{img.src}</small>
                      </div>
                    </div>
                  ))}
                </CollapsibleList>
              );
            })()}

            {testName === 'image_alt' && auditData.images && hasIssues && (() => {
              const missingAltImages = auditData.images.filter(img => !img.alt || img.alt.trim() === '');
              return (
                <CollapsibleList count={missingAltImages.length} label={`${missingAltImages.length} images missing alt attribute`}>
                  {missingAltImages.map((img, idx) => (
                    <div key={idx} className="flex justify-between items-start mb-3 pb-3 border-b last:border-0">
                      <div className="flex-1 text-truncate">
                        <p className="text-sm mb-1">
                          <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono mr-2">{img.extension || 'N/A'}</span>
                          {img.title || 'No title set'}
                        </p>
                        <small className="text-gray-500 truncate block">{img.src}</small>
                      </div>
                    </div>
                  ))}
                </CollapsibleList>
              );
            })()}

            {testName === 'image_lazy_loading' && auditData.images && hasIssues && (() => {
              const missingLazyImages = auditData.images.filter(img => !img.loading || img.loading !== 'lazy');
              return (
                <CollapsibleList count={missingLazyImages.length} label={`${missingLazyImages.length} images missing lazy loading`}>
                  {missingLazyImages.map((img, idx) => (
                    <div key={idx} className="flex justify-between items-start mb-3 pb-3 border-b last:border-0">
                      <div className="flex-1 text-truncate">
                        <p className="text-sm mb-1">
                          <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono mr-2">{img.extension || 'N/A'}</span>
                          {img.title || 'No title set'}
                        </p>
                        <small className="text-gray-500 truncate block">{img.src}</small>
                      </div>
                    </div>
                  ))}
                </CollapsibleList>
              );
            })()}

            {testName === 'social_links' && auditData.social_links && auditData.social_links.length > 0 && (
              <CollapsibleList count={auditData.social_links.length} label={`${auditData.social_links.length} social links found`}>
                <ol className="list-decimal pl-5 space-y-2">
                  {auditData.social_links.map((link, idx) => (
                    <li key={idx} className="text-sm">
                      <span className="capitalize font-medium">{link.type}</span>
                      <a href={link.href} target="_blank" rel="nofollow noreferrer" className="text-blue-600 hover:underline ml-2 block truncate">
                        {link.href}
                      </a>
                      {link.text && <small className="text-gray-500">{link.text}</small>}
                    </li>
                  ))}
                </ol>
              </CollapsibleList>
            )}

            {testName === 'internal_links' && auditData.links && hasIssues && (() => {
              const internalLinks = auditData.links.filter(link => link.is_internal);
              return (
                <CollapsibleList count={internalLinks.length} label={`${internalLinks.length} internal links found`}>
                  {internalLinks.slice(0, 50).map((link, idx) => (
                    <div key={idx} className="mb-2 pb-2 border-b last:border-0">
                      <a href={link.href} target="_blank" rel="nofollow noreferrer" className="text-sm text-blue-600 hover:underline block truncate">
                        {link.text || link.title || link.href}
                      </a>
                    </div>
                  ))}
                  {internalLinks.length > 50 && (
                    <p className="text-sm text-gray-500 mt-2">... and {internalLinks.length - 50} more</p>
                  )}
                </CollapsibleList>
              );
            })()}

            {testName === 'external_links' && auditData.links && hasIssues && (() => {
              const externalLinks = auditData.links.filter(link => !link.is_internal);
              return (
                <CollapsibleList count={externalLinks.length} label={`${externalLinks.length} external links found`}>
                  {externalLinks.slice(0, 50).map((link, idx) => (
                    <div key={idx} className="mb-2 pb-2 border-b last:border-0">
                      <a href={link.href} target="_blank" rel="nofollow noreferrer" className="text-sm text-blue-600 hover:underline block truncate">
                        {link.text || link.title || link.href}
                      </a>
                    </div>
                  ))}
                  {externalLinks.length > 50 && (
                    <p className="text-sm text-gray-500 mt-2">... and {externalLinks.length - 50} more</p>
                  )}
                </CollapsibleList>
              );
            })()}

            {testName === 'unsafe_external_links' && auditData.links && hasIssues && (() => {
              const unsafeLinks = auditData.links.filter(link => link.is_unsafe);
              return (
                <CollapsibleList count={unsafeLinks.length} label={`${unsafeLinks.length} unsafe links found`}>
                  {unsafeLinks.map((link, idx) => (
                    <div key={idx} className="mb-3 pb-3 border-b last:border-0">
                      <a href={link.href} target="_blank" rel="nofollow noreferrer" className="text-sm text-blue-600 hover:underline block truncate">
                        {link.text || link.title || link.href}
                      </a>
                      <small className="text-red-600">Missing: rel="noopener" or rel="noreferrer"</small>
                    </div>
                  ))}
                </CollapsibleList>
              );
            })()}

            {testName === 'non_deferred_scripts' && auditData.scripts && hasIssues && (() => {
              const nonDeferredScripts = auditData.scripts.filter(script => !script.is_deferred);
              return (
                <CollapsibleList count={nonDeferredScripts.length} label={`${nonDeferredScripts.length} scripts without defer`}>
                  {nonDeferredScripts.map((script, idx) => (
                    <div key={idx} className="mb-2 pb-2 border-b last:border-0">
                      <p className="text-sm">
                        <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono mr-2">{script.type}</span>
                        {script.src ? (
                          <span className="text-gray-700 truncate block">{script.src}</span>
                        ) : (
                          <span className="text-gray-500">Embedded script tag</span>
                        )}
                      </p>
                    </div>
                  ))}
                </CollapsibleList>
              );
            })()}

            {testName === 'inline_css' && auditData.inline_css && hasIssues && (
              <CollapsibleList count={auditData.inline_css.length} label={`${auditData.inline_css.length} inline styles found`}>
                {auditData.inline_css.slice(0, 20).map((css, idx) => (
                  <div key={idx} className="mb-2 pb-2 border-b last:border-0">
                    <p className="text-xs">
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono mr-2">&lt;{css.tag}&gt;</span>
                      <code className="text-gray-700">{css.style}</code>
                    </p>
                  </div>
                ))}
                {auditData.inline_css.length > 20 && (
                  <p className="text-sm text-gray-500 mt-2">... and {auditData.inline_css.length - 20} more</p>
                )}
              </CollapsibleList>
            )}

            {testName === 'deprecated_html_tags' && auditData.deprecated_html_tags && hasIssues && auditData.deprecated_html_tags.length > 0 && (
              <CollapsibleList count={auditData.deprecated_html_tags.length} label={`${auditData.deprecated_html_tags.length} deprecated tags found`}>
                {auditData.deprecated_html_tags.map((tag, idx) => (
                  <div key={idx} className="mb-1">
                    <code className="text-sm bg-red-50 px-2 py-1 rounded text-red-700">&lt;{tag}&gt;</code>
                  </div>
                ))}
              </CollapsibleList>
            )}

            {testName === 'emails' && auditData.emails && hasIssues && auditData.emails.length > 0 && (
              <CollapsibleList count={auditData.emails.length} label={`${auditData.emails.length} plaintext emails found`}>
                {auditData.emails.map((email, idx) => (
                  <div key={idx} className="mb-1">
                    <code className="text-sm bg-yellow-50 px-2 py-1 rounded text-yellow-700">{email}</code>
                  </div>
                ))}
              </CollapsibleList>
            )}

            {/* Display issues */}
            {Object.entries(testDef.issues).map(([issueType, issueDef]) => {
              const hasMajor = issues.major && Array.isArray(issues.major[testName]) && issues.major[testName].includes(issueType);
              const hasModerate = issues.moderate && Array.isArray(issues.moderate[testName]) && issues.moderate[testName].includes(issueType);
              const hasMinor = issues.minor && Array.isArray(issues.minor[testName]) && issues.minor[testName].includes(issueType);

              if (hasMajor) {
                return <div key={issueType}>{renderIssue('major', issueDef.message, issueDef.help)}</div>;
              } else if (hasModerate) {
                return <div key={issueType}>{renderIssue('moderate', issueDef.message, issueDef.help)}</div>;
              } else if (hasMinor) {
                return <div key={issueType}>{renderIssue('minor', issueDef.message, issueDef.help)}</div>;
              }
              return null;
            })}
          </div>
        </div>
      </div>
    );
  };

  // Group tests by category as they appear in example.report.html.php
  const categories = {
    'General': ['language', 'meta_charset', 'meta_viewport', 'favicon', 'doctype', 'deprecated_html_tags', 'header_server', 'server_compression'],
    'SEO': ['title', 'meta_description', 'h1', 'meta_keywords', 'meta_robots', 'header_robots', 'robots', 'not_found', 'canonical', 'is_seo_friendly_url', 'opengraph', 'schemas', 'other_headings'],
    'Performance': ['response_time', 'page_size', 'dom_size', 'http_requests', 'is_http2'],
    'Security': ['is_https', 'is_ssl_valid', 'safe_browsing'],
    'Content': ['inline_css', 'image_formats', 'image_alt', 'image_lazy_loading', 'unsafe_external_links', 'non_deferred_scripts', 'internal_links', 'external_links', 'social_links', 'words_count', 'words_used', 'emails', 'text_to_html_ratio']
  };

  return (
    <div className="space-y-6">
      {Object.entries(categories).map(([categoryName, tests]) => {
        const categoryTests = tests.map(testName => {
          const testDef = testDefinitions[testName];
          if (!testDef) return null;
          return renderTest(testName, testDef);
        }).filter(Boolean);

        if (categoryTests.length === 0) return null;

        return (
          <div key={categoryName} className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-gray-700">{categoryName}</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                  {categoryTests.length} {categoryTests.length === 1 ? 'test' : 'tests'}
                </span>
              </div>
            </div>
            <div className="p-6">
              {categoryTests}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AuditReportSection;
